import { useEffect, useState } from 'react';

import { Calendar, FileText, PencilRuler, Trash2 } from 'lucide-react';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDraftCases } from '@/api/queries/useGetDraftCases';
import { Button } from '@/components/atoms/new-button';
import EmptyState from '@/components/molecules/EmptyState';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Case } from '@/types';

interface DraftsModalContentProps {
  matterId: string;
  onSelectDraft: (caseId: string) => void;
  onDeleteDraft?: (caseId: string) => void;
}

const DraftCard = ({
  draft,
  onClick,
  onDelete,
  canDelete,
}: {
  draft: Case;
  onClick: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
}) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative w-full rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-md',
        'mb-2 flex cursor-pointer flex-col',
      )}
      tabIndex={0}
      role="button"
      aria-label={`Open draft case ${draft.name || 'Untitled'}`}
    >
      <div className="flex w-full items-start justify-between gap-4 rounded-t-lg border-b border-dashed border-gray-200 bg-gray-100 px-4 pt-4">
        <div className="min-w-0 flex-1">
          <div className="max-w-full truncate pb-2 text-lg font-bold text-gray-800 group-hover:text-blue-600">
            {draft.name ? draft.name : <span className="text-gray-600 italic">Untitled</span>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {canDelete && (
            <Button
              data-testid="delete-draft-btn"
              variant="ghost"
              size="sm"
              className="ml-1 h-8 w-8 text-gray-400 hover:bg-red-50 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
              aria-label="Delete draft case"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Legal Issues Preview */}
      <div className="p-4">
        {draft.legalIssues && (
          <div className="mt-3 flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <div className="line-clamp-1 max-w-full truncate text-sm leading-relaxed text-gray-700">
              {draft.legalIssues}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-end justify-between">
          {/* Date */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar className="h-3 w-3" />
            <span>Created {formatDate(draft.createdDate)}</span>
          </div>
          <div className="flex-shrink-0">
            <span className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-normal text-gray-500 shadow-sm">
              <PencilRuler className="h-3 w-3" />
              <span>Draft</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DraftsModalContent = ({ matterId, onSelectDraft, onDeleteDraft }: DraftsModalContentProps) => {
  const [drafts, setDrafts] = useState<Case[]>([]);

  const { data: draftsData, isLoading } = useGetDraftCases(matterId);

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();

  const handleDeleteDraft = (id: string) => {
    onDeleteDraft?.(id);
    setDrafts(drafts.filter((draft) => draft.id !== id));
  };

  useEffect(() => {
    if (draftsData && draftsData.length > 0) {
      setDrafts(draftsData);
    }
  }, [draftsData]);

  return (
    <div className="p-6" data-testid="drafts-modal">
      <h2 className="mb-4 flex items-center gap-2 border-b-2 pb-2 text-xl font-semibold text-gray-900">
        <PencilRuler className="h-6 w-6" /> Draft Cases
      </h2>
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <></>
        ) : !draftsData || draftsData?.length === 0 ? (
          <EmptyState title="No drafts found" subtitle="You don't have any draft cases at the moment." />
        ) : (
          <div className="grid gap-4 py-4">
            {drafts.map((draft, index) => (
              <div data-testid={`draft-case-${index}`} data-case-id={draft.id} key={draft.id}>
                <DraftCard
                  draft={draft}
                  onClick={() => onSelectDraft(draft.id)}
                  onDelete={() => {
                    trackCustomEvent(TrackingEvents.DeleteDraftCase, {
                      caseId: draft.id,
                    });
                    handleDeleteDraft(draft.id);
                  }}
                  canDelete={hasPermission(Permission.CaseDeleteAny) || user?.id === draft.createdById}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftsModalContent;
